import { Font } from '@react-pdf/renderer';
import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { DevelopmentTimelineTable } from 'PDF/components/DevelopmentTimeline/DevelopmentTimelineTable';
import { GoalsTable } from 'PDF/components/GoalsTable/GoalsTable';
import { PDFPage } from 'PDF/components/Page';
import { ITextItem, PdfContentText } from 'PDF/components/PdfContentText';
import { SubChapterHeading } from 'PDF/components/SubChapterHeading';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { Noto } from 'PDF/fonts';
import { getSectionInfo } from 'PDF/helpers/sections';
import {
  getLegalFrameworkAndRisksChapterNames,
  LegalFrameworkAndRisksKeys,
  LegalFrameworkAndRisksSectionAvailability,
} from 'PDF/types/pdfSections';

import { getSubSectionHeading } from 'pages/BusinessToolPage/PdfPreview/utils';

import { ILegalFrameworkAndRisksPdfData } from './types';
import { SectionCoverPagePdf } from '../SectionCoverPage';
import { getNextSectionIndex } from '../TableOfContents/helpers';

Font.getRegisteredFonts();
Font.register(Noto);

export interface ILegalFrameworkAndRisksPdfProps {
  magazineItems: ITextItem[][];
  data: ILegalFrameworkAndRisksPdfData;
  businessPlanType: IBusinessToolType;
  sectionPicture?: string;
  isStandardOrLightTariff: boolean;
  isExamplePage: boolean;
}

export const LegalFrameworkAndRisksPdf = (
  props: ILegalFrameworkAndRisksPdfProps & {
    availability: LegalFrameworkAndRisksSectionAvailability;
    isPreviewMode?: boolean;
    sectionNumber: string;
  },
) => {
  const {
    magazineItems,
    data,
    sectionPicture,
    availability,
    isPreviewMode = false,
    sectionNumber,
    isStandardOrLightTariff,
    isExamplePage,
  } = props;
  const { sectionHeading } = getSectionInfo(
    BUSINESS_PLAN_TYPES.FOR_BANK,
    PDF_SECTIONS.LEGAL_FRAMEWORK_AND_RISKS,
    isExamplePage,
  );
  const getSubHeading = getSubSectionHeading(sectionNumber);
  const getSubChapterIndex = getNextSectionIndex();
  const LEGAL_FRAMEWORK_AND_RISKS_CHAPTER_NAMES = getLegalFrameworkAndRisksChapterNames(isExamplePage);
  const subChapterNames = (Object.keys(availability) as LegalFrameworkAndRisksKeys[])
    .filter((key) => !!availability[key])
    .map((key) => `${Number(sectionNumber)}.${getSubChapterIndex()}. ${LEGAL_FRAMEWORK_AND_RISKS_CHAPTER_NAMES[key]}`);
  const isSectionEmpty = Object.values(availability).every((value) => !value);
  const isCoverPageEmpty = isSectionEmpty && isPreviewMode;

  if (!data) {
    return (
      <SectionCoverPagePdf
        header={sectionHeading}
        sectionNumber={sectionNumber}
        sectionId="legalFrameworkAndRisks"
        background={sectionPicture ?? ''}
        highlightSettings={{ position: 2, variant: 'after' }}
        subChapterNames={subChapterNames}
        isEmpty={isCoverPageEmpty}
        isStandardOrLightTariff={isStandardOrLightTariff}
        isExamplePage={isExamplePage}
      />
    );
  }

  const { listOfActions, listOfRisks } = data;

  return (
    <>
      {(!isSectionEmpty || isCoverPageEmpty) && (
        <SectionCoverPagePdf
          header={sectionHeading}
          sectionNumber={sectionNumber}
          sectionId="legalFrameworkAndRisks"
          background={sectionPicture ?? ''}
          highlightSettings={{ position: 2, variant: 'after' }}
          subChapterNames={subChapterNames}
          isEmpty={isCoverPageEmpty}
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      )}
      {!isSectionEmpty && (
        <PDFPage
          shouldShowHeaderAndFooter
          sectionNumber={sectionNumber}
          sectionTitle={sectionHeading}
          withEmptySpaceImage
          isStandardOrLightTariff={isStandardOrLightTariff}
        >
          {availability.generalLaws && (
            <>
              <SubChapterHeading
                id="generalLaws"
                heading={getSubHeading(LEGAL_FRAMEWORK_AND_RISKS_CHAPTER_NAMES.generalLaws)}
              />
              <PdfContentText items={magazineItems[0]} />
            </>
          )}
          {availability.industryRequirements && (
            <>
              <SubChapterHeading
                id="industryRequirements"
                heading={getSubHeading(LEGAL_FRAMEWORK_AND_RISKS_CHAPTER_NAMES.industryRequirements)}
              />
              <PdfContentText items={magazineItems[1]} />
            </>
          )}
          {availability.legalRequirements && (
            <>
              <SubChapterHeading
                id="legalRequirements"
                heading={getSubHeading(LEGAL_FRAMEWORK_AND_RISKS_CHAPTER_NAMES.legalRequirements)}
              />
              <PdfContentText items={magazineItems[2]} />
            </>
          )}
          {availability.listOfActions && (
            <>
              <SubChapterHeading
                id="listOfActions"
                heading={getSubHeading(LEGAL_FRAMEWORK_AND_RISKS_CHAPTER_NAMES.listOfActions)}
              />
              {listOfActions.length > 0 && (
                <DevelopmentTimelineTable items={listOfActions} withLegend={false} isExamplePage={isExamplePage} />
              )}
            </>
          )}
          {availability.listOfRisks && (
            <>
              <SubChapterHeading
                minPresenceAhead={400}
                id="listOfRisks"
                heading={getSubHeading(LEGAL_FRAMEWORK_AND_RISKS_CHAPTER_NAMES.listOfRisks)}
              />
              <GoalsTable tableData={listOfRisks} />
            </>
          )}
        </PDFPage>
      )}
    </>
  );
};
